/**
 * Formulaire d'un marqueur — création et modification.
 *
 * Le même formulaire pour les deux : les champs sont identiques, et deux
 * formulaires jumeaux divergeraient à la première correction. `editing` décide
 * du libellé du bouton et de la présence de « Annuler ».
 *
 * ── L'état initial vient des props, sans useEffect de synchronisation ───────
 * La page remonte le composant avec une `key` liée au marqueur en cours
 * d'édition. Cliquer « Modifier » sur une autre ligne crée donc un nouveau
 * composant, dont l'état part des bonnes valeurs. Un `useEffect` qui recopie
 * les props dans l'état écraserait au passage ce que l'utilisateur vient de
 * taper, à chaque rendu du parent.
 *
 * ── Les bornes de saisie ne sont pas réécrites ici ──────────────────────────
 * `maxLength` et la liste des natures viennent de `@shared/types/stats`, donc
 * de la même source que la validation Zod du backend et que la contrainte
 * CHECK de la table. Un troisième jeu de valeurs en dur serait le premier à
 * divergér.
 */

import { useState, type FormEvent } from 'react';

import {
  MARKER_KINDS,
  MARKER_KIND_LABELS,
  MARKER_LABEL_MAX_LENGTH,
  MARKER_NOTE_MAX_LENGTH,
  MARKER_SCOPES,
  MARKER_SCOPE_LABELS,
  type MarkerScope,
} from '@shared/types/stats';

import { todayInParis } from '../lib/dates';
import type { Marker, MarkerInput, MarkerKind } from '../types';
import { Card } from './Card';

interface MarkerFormProps {
  /** Marqueur à modifier, ou `null` pour une création. */
  editing: Marker | null;
  isSaving: boolean;
  onSubmit: (input: MarkerInput) => Promise<void>;
  onCancel: () => void;
}

export function MarkerForm({ editing, isSaving, onSubmit, onCancel }: MarkerFormProps) {
  const [date, setDate] = useState(editing?.date ?? todayInParis());
  const [label, setLabel] = useState(editing?.label ?? '');
  const [kind, setKind] = useState<MarkerKind>(editing?.kind ?? 'release');
  // Où s'affiche le trait : analyses, ventes, ou les deux (défaut d'un nouveau marqueur).
  const [scope, setScope] = useState<MarkerScope>(editing?.scope ?? 'both');
  const [note, setNote] = useState(editing?.note ?? '');

  // L'erreur d'ecriture est affichee DANS le formulaire : un 409 (« un
  // marqueur porte deja ce libelle a cette date ») se corrige dans le champ
  // qui l'a provoque, pas en haut de page.
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    try {
      await onSubmit({ date, label, kind, scope, note: note.trim() ? note : null });

      // En creation seulement : on vide le libelle et la note, on GARDE la
      // date, la nature et l'affichage. Poser trois marqueurs du meme jour est le cas
      // courant (une mise a jour, une campagne, un tarif), et remettre la date
      // a zero obligerait a la ressaisir chaque fois.
      if (!editing) {
        setLabel('');
        setNote('');
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "L'enregistrement a échoué.");
    }
  };

  return (
    <Card
      title={editing ? 'Modifier le marqueur' : 'Poser un marqueur'}
      subtitle="Un jour, un libellé, une nature, et où l'afficher — le trait apparaît aussitôt sur les courbes datées de ces pages."
      className="marker-form-card"
    >
      <form className="marker-form" onSubmit={(event) => void handleSubmit(event)}>
        <div className="field">
          <label className="field__label" htmlFor="marker-date">
            Date de l'évènement
          </label>
          <input
            id="marker-date"
            type="date"
            required
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="marker-kind">
            Nature
          </label>
          <select
            id="marker-kind"
            value={kind}
            onChange={(event) => setKind(event.target.value as MarkerKind)}
          >
            {MARKER_KINDS.map((value) => (
              <option key={value} value={value}>
                {MARKER_KIND_LABELS[value]}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="marker-scope">
            Afficher sur
          </label>
          <select id="marker-scope" value={scope} onChange={(event) => setScope(event.target.value as MarkerScope)}>
            {MARKER_SCOPES.map((value) => (
              <option key={value} value={value}>
                {MARKER_SCOPE_LABELS[value]}
              </option>
            ))}
          </select>
        </div>

        <div className="field marker-form__grow">
          <label className="field__label" htmlFor="marker-label">
            Libellé
          </label>
          <input
            id="marker-label"
            type="text"
            required
            maxLength={MARKER_LABEL_MAX_LENGTH}
            placeholder="Sortie 2.4 — nouveau mode apprentissage"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
        </div>

        <div className="field marker-form__grow">
          <label className="field__label" htmlFor="marker-note">
            Note (facultatif)
          </label>
          <textarea
            id="marker-note"
            rows={2}
            maxLength={MARKER_NOTE_MAX_LENGTH}
            placeholder="Ce qu'il faut savoir pour relire ce marqueur dans six mois."
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        <div className="marker-form__actions">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={isSaving || label.trim().length === 0}
          >
            {editing ? 'Enregistrer' : 'Ajouter'}
          </button>

          {editing && (
            <button type="button" className="btn" onClick={onCancel} disabled={isSaving}>
              Annuler
            </button>
          )}
        </div>

        {error && (
          <p className="login__error marker-form__error" role="alert">
            {error}
          </p>
        )}
      </form>
    </Card>
  );
}
